'use client';

import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import {
  TypeTabContent,
  TypeTabContentFields,
} from '@/core/generated-types/TypeTabContent';

import { TypeTabNavigationFields } from '@/core/generated-types/TypeTabNavigation';

import { CLICKED_ITEM_TYPES, trackItemClickedEvent } from '@/utils/rudderstack';

import { DynamicComponent } from '../DynamicComponent';

import { TabContent } from './TabContent';
import TabNavigationSkeleton from './TabNavigationSkeleton';

export interface EnhancedTypeTabContent extends TypeTabContent {
  fields: TypeTabContentFields & {
    customComponent?: React.ReactNode;
    onClick?: () => void;
  };
}

type TabNavigationProps = Omit<TypeTabNavigationFields, 'entryName'> & {
  eventPageLocation?: string;
  titleClassName?: string;
  showSkeletonOnLoad?: boolean;
};

const SESSION_STORAGE_KEY = 'TabNavigationSelectedTab';

const TabNavigation: React.FC<TabNavigationProps> = ({
  title,
  titleClassName,
  tabs,
  tabType = 'pill',
  eventPageLocation,
  showSkeletonOnLoad = true,
}) => {
  const [storedSelectedTab, setStoredSelectedTab] = useState<
    string | undefined
  >();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const searchParamsSelectedTab = searchParams.get('selected_tab');

    if (searchParamsSelectedTab) {
      setStoredSelectedTab(searchParamsSelectedTab);
    } else {
      const sessionStorageSelectedTab = sessionStorage.getItem(
        `${SESSION_STORAGE_KEY}[${location.pathname}]`,
      );

      if (sessionStorageSelectedTab) {
        setStoredSelectedTab(sessionStorageSelectedTab);
      }
    }
  }, []);

  const fallbackSelectedTab = tabs[0].fields.id;

  const defaultSelectedTab = !showSkeletonOnLoad
    ? fallbackSelectedTab
    : undefined;

  const [selectedTab, setSelectedTab] = useState<string | undefined>(
    defaultSelectedTab,
  );

  useEffect(() => {
    setSelectedTab(storedSelectedTab || fallbackSelectedTab);
  }, [storedSelectedTab, fallbackSelectedTab]);

  useEffect(() => {
    if (selectedTab) {
      sessionStorage.setItem(
        `${SESSION_STORAGE_KEY}[${location.pathname}]`,
        selectedTab,
      );
    }
  }, [selectedTab]);

  const components = useMemo(
    () => tabs.find((tab) => tab.fields.id === selectedTab)?.fields.components,
    [selectedTab, tabs],
  );

  if (!selectedTab) return <TabNavigationSkeleton title={title} />;

  const handleTabClick = (tab: TypeTabContent) => {
    setSelectedTab(tab.fields.id);

    trackItemClickedEvent({
      url: window.location.href,
      type: CLICKED_ITEM_TYPES.TAB,
      pageLocation: eventPageLocation || title || 'Unspecified',
      cta: `${tab.fields.label} ${tab.fields.subLabel}`.trim(),
    });
  };

  return (
    <div
      className="w-full flex flex-col justify-center items-center"
      data-testid="tab-navigation"
      id="tab-navigation"
    >
      {title && (
        <h3
          className={classNames(
            titleClassName
              ? titleClassName
              : 'w-full px-5 pt-8 lg:pt-20 pb-2 lg:pb-10 text-h1 lg:text-5xl font-bold text-center',
          )}
        >
          {title}
        </h3>
      )}
      <ul
        className={classNames(
          'py-3 grid grid-flow-col auto-cols-fr overflow-x-scroll no-scrollbar max-w-full',
          {
            'space-x-2': tabType !== 'pill',
          },
        )}
      >
        {tabs.map((tab: TypeTabContent, index: number) => (
          <li key={tab.fields.id}>
            <button
              data-testid={`tab-button-${tab.fields.id}`}
              className="w-full"
              onClick={() => handleTabClick(tab)}
            >
              <TabContent
                tab={tab}
                isPill={tabType === 'pill'}
                isActive={selectedTab === tab.fields.id}
                isFirst={index === 0}
                isLast={index === tabs.length - 1}
              />
            </button>
          </li>
        ))}
      </ul>

      {tabs.map((tab: EnhancedTypeTabContent) => {
        if (selectedTab !== tab.fields.id) {
          return null;
        }

        if (selectedTab === tab.fields.id) {
          if (tab.fields.customComponent) {
            return (
              <div
                key={tab.fields.id}
                className="w-full"
                data-testid="tab-navigation-custom-component"
              >
                {tab.fields.customComponent}
              </div>
            );
          } else {
            return (
              <div
                data-testid="tab-navigation-dynamic-component"
                key={tab.fields.id}
              >
                {components?.map((component: any, i: number) => (
                  <DynamicComponent
                    key={`${component.sys.id}-${i}`}
                    component={component}
                    removeContainer
                  />
                ))}
              </div>
            );
          }
        }
      })}
    </div>
  );
};

export default TabNavigation;
